/**
 *
 * @module eventSchemata
 *
 */

import Check from 'check-types';
import EventSchema from './eventSchema';
import Services from './../services/entry';

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    boolean,
    object
} = Check.assert;

/**
 *
 * @class EventSchemata
 * @desc A collection of `SDK.UserActivity.EventSchema` used by `SDK.UserActivity.EventSchemataProvider` when
 * constructing a payload
 * @note this is entirely private and is constructed within `SDK.UserActivity.EventSchemataProvider`
 *
 */
export default class EventSchemata {

    /**
     *
     * @access private
     * @param {Array<SDK.UserActivity.EventSchema>} schemata
     * @param {Boolean} passthrough
     *
     */
    constructor(schemata, passthrough) {

        const errorMessage = {
            ctor: 'EventSchemata(schemata, passthrough)',
            get schemata() {
                return `${this.ctor} schemata should be an Array of SDK.UserActivity.EventSchema`;
            },
            get passthrough() {
                return `${this.ctor} passthrough should be a Boolean`;
            }
        };

        array.of.instanceStrict(schemata, EventSchema, errorMessage.schemata, InvalidArgumentException);
        boolean(passthrough, errorMessage.passthrough, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {Array<SDK.UserActivity.EventSchema>}
         *
         */
        this.schemata = schemata;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc determines if we should apply global tracking parameters when the payload is generated
         *
         */
        this.passthrough = passthrough;

    }

    /**
     *
     * @access private
     * @param {Object} trackingParameters
     * @note `trackingParameters` should be cloned to avoid modifying it
     * @note this will loop through all of the available schema and generate all possible individual values
     * first it will potentially merge in the global tracking params and then start to iterate through the schemata
     * which in turn will iterate through each individual schemas values
     * @note we keep track of any failures that occur during this loop but since we are merging the object each time
     * we need to store an external variable outside of the loop to keep track of the failures
     * @returns {Object}
     *
     */
    generateEventPayload(trackingParameters) {

        const errorMessage = {
            method: 'EventSchemata.generateEventPayload(trackingParameters)',
            get trackingParameters() {
                return `${this.method} trackingParameters should be an Object`;
            }
        };

        object(trackingParameters, errorMessage.trackingParameters, InvalidArgumentException);

        const { schemata, passthrough } = this;

        const _failures = [];

        let eventPayload = passthrough ? trackingParameters : {};

        for (const schema of Object.keys(schemata)) {

            const schemaGeneratedData = schemata[schema].generateEventData(trackingParameters);

            if (Check.nonEmptyArray(schemaGeneratedData._failures)) {

                _failures.push(...schemaGeneratedData._failures);

                // not necessary but cleaner to remove it and add back the aggregate _failures array before returning
                delete schemaGeneratedData._failures;

            }

            eventPayload = Object.assign(eventPayload, schemaGeneratedData);

        }

        // since `eventPayload._failures` is overwritten in each loop we have to keep track and rewrite before returning
        if (Check.nonEmptyArray(_failures)) {
            eventPayload._failures = _failures;
        }

        return eventPayload;

    }

}



// WEBPACK FOOTER //
// ./src/userActivity/eventSchemata.js